import React from 'react';
import { Button } from '@brighterion/br-ui';
import { handleKeyEvent, handleOnClickEvent } from '@brighterion/utils/general';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import { getEmptyStringReplacementText } from '@brighterion/utils/template-strings';
import { NumberService } from 'Services';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import PageHeader from 'Components/PageHeader';
import Overview from 'Components/Overview';
import ListEntryTable from 'Routes/ListManagement/components/ListEntryTable';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import Layout from 'Components/Layout';
import ButtonGroup from 'Components/ButtonGroup';
import useGetSingleListAndSetBreadcrumb from '../hooks/useGetSingleListAndSetBreadcrumb';
import styles from './ViewListEntriesRemovedPage.css';

type GenOverview = { id: string; label: string; data: string | number | React.ReactNode; noEllipsis?: boolean };

export const genOverview = (apiData: ListManagementApiList, t: (s: String) => string): GenOverview[] => [
    {
        id: 'listName',
        label: t('List Name'),
        data: apiData?.name
    },
    {
        id: 'removedEntries',
        label: t('Removed Entries'),
        data: NumberService.getLocaleNumber(Number(apiData?.inactiveListEntriesCount) || 0)
    },
    {
        id: 'listType',
        label: t('List Type'),
        data: apiData?._formatted?.listType
    },
    {
        id: 'description',
        label: t('Description'),
        data: apiData?.description
    }
];

const ViewListEntriesRemovedPage = () => {
    const { t } = useTranslation();
    const { users } = useGetReduxUsers();
    const history = useHistory();
    const { query: singleListAPI, listId } = useGetSingleListAndSetBreadcrumb(users);
    const { hasError, errorCmp } = errorController([singleListAPI.error]);
    const overviewData = genOverview(singleListAPI.data as ListManagementApiList, t);

    if (singleListAPI.isLoading) {
        return <RouteLoading />;
    }

    if (hasError) {
        return errorCmp;
    }

    return (
        <Layout>
            <PageHeader>
                <PageHeader.Left className={styles.titleText}>{t('Removed Entries')}</PageHeader.Left>
                <PageHeader.Right>
                    <ButtonGroup direction='row'>
                        <Button
                            className={styles.backBtn}
                            onKeyDown={handleKeyEvent(() => history.push(`${ROUTES.LIST_MANAGEMENT.INDEX}/${listId}`))}
                            onClick={handleOnClickEvent(() =>
                                history.push(`${ROUTES.LIST_MANAGEMENT.INDEX}/${listId}`)
                            )}
                            variant='tertiary'
                            size='small'
                            iconName='arrow-left-1'
                        >
                            {t('Back To List')}
                        </Button>
                    </ButtonGroup>
                </PageHeader.Right>
            </PageHeader>

            <Overview dataTestId='removed-entries-overview' leftTitle='List Overview' leftData={overviewData} />

            <ListEntryTable
                listType={singleListAPI.data?.listType as SingleListType}
                listId={listId}
                listName={getEmptyStringReplacementText(singleListAPI.data?.name)}
                merchantSpecific={Boolean(singleListAPI.data?.merchantSpecific)}
                users={users}
            />
        </Layout>
    );
};

export default ViewListEntriesRemovedPage;
